/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { HelpOutlineTwoTone } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box, Paper, Popper, Tooltip, Typography, useMediaQuery
} from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import { useCloud } from "../../Hooks/Cloud";
import StatusBadge from "../StatusBadge";

/**
 * Monitor sub-group accordion component
 * Description: This component is used to display the monitor subgroups in the accordion
 */
function SubGroupAccordion({ name, description, status, availability, availabilityHistory = [] }) {
  const { cloud } = useCloud();
  const features = window.ENV[cloud].features;

  const [ popperOpen, setPopperOpen ] = React.useState(false);
  const [ anchorEl, setAnchorEl ] = React.useState(null);
  const [ popperDate, setPopperDate ] = React.useState(null);
  const [ popperAvailability, setPopperAvailability ] = React.useState(null);

  const availabilityPercentage = availability.find((entry) => entry.duration === "Last 90 days")?.availability;

  const nameWords = (name || "").trim().split(/\s+/);
  const nameLastWord = nameWords.pop();
  const nameLeadingWords = nameWords.join(" ");

  const minWidth768 = useMediaQuery("(min-width:768px)");
  const maxWidth768 = useMediaQuery("(max-width:768px)");

  return (
    <Accordion
      expanded={ minWidth768 && features.availabilityHistory && availabilityHistory.length > 0 }
      disableGutters={ false }
      square={ false }
      variant="outlined"
      sx={ { border: 0 } }
      style={ { cursor: "default" } }
    >
      <AccordionSummary style={ { cursor: "default" } }>
        <Typography variant="body2" sx={ { flex: 1, minWidth: 0, pr: 2 } }>
          { /* Render the name inline and tooltip icon sits right after the text */ }
          <Box component="span" sx={ { display: "block" } }>
            { nameLeadingWords && `${nameLeadingWords} ` }
            { /* Keep the last word and the icon together so the icon never wraps onto its own line */ }
            <Box component="span" sx={ { whiteSpace: "nowrap" } }>
              { nameLastWord }
              { /* Tooltip component to show the description of the monitors */ }
              { description && (
                <Tooltip title={ description } arrow style={ { cursor: "pointer" } }>
                  <HelpOutlineTwoTone color="disabled" fontSize="small" sx={ { ml: 0.5, verticalAlign: "middle" } } />
                </Tooltip>
              ) }
            </Box>
          </Box>

          { /* Render and show the last 90 days availability if exists */ }
          { availabilityPercentage && (
            <Box component="span" sx={ { alignItems: "center",display: "flex" } }>
              <Box component="span">
                <Typography variant="caption">
                  { parseFloat(
                    Math.floor(availabilityPercentage * 10 ** window.ENV.uptimePrecision) /
                      10 ** window.ENV.uptimePrecision
                  ) }
                  % (Last 90 days)
                </Typography>
              </Box>
            </Box>
          ) }
        </Typography>

        { /* Render and show the availability history of the monitor */ }
        { /* No fixed width: the inline history below is disabled, and reserving space squeezes the name column */ }
        <Box variant="span" sx={ { flexShrink: 0, textAlign: "justify" } }>
          { /* Display outage history inline */ }
          { /* { (features.availabilityHistory && availabilityHistory.length > 0) && (
            (<Box display="flex" flexDirection="row" justifyContent="right" alignItems="flex-end">
              { availabilityHistory.map((entry, index) => (
                <Box
                  key={ index }
                  overflow={ "hidden" }
                  bgcolor={ parseFloat(entry.availability) === 100 ? "#28a745" : "#dc3545" }
                  borderRadius="4px"
                  justifyContent={ "flex-end" }
                  width={ "3px" }
                  height={ parseFloat(entry.availability) === 100 ? "5px" : "15px" }
                  mr={ 0.3 }
                  aria-label={ `Availability on ${ entry.date } is ${ entry.availability }%` }
                  onMouseEnter={ event => {
                    setPopperDate(entry.date);
                    setPopperAvailability(entry.availability);
                    setAnchorEl(event.currentTarget);
                    setPopperOpen(true);
                  } }
                  onMouseLeave={ () => {
                    setPopperOpen(false);
                  } }
                >
                  <Popper open={ popperOpen } anchorEl={ anchorEl }>
                    <Paper sx={ { p: 1 } } variant="outlined">
                      <Typography variant="caption">{ new Date(popperDate)
                        .toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) }
                      </Typography>
                      <Typography variant="body2" sx={ { fontWeight: "bold" } }>
                        { parseFloat( Math.floor(popperAvailability * 10 ** window.ENV.uptimePrecision) /
                            10 ** window.ENV.uptimePrecision
                        ).toFixed(window.ENV.uptimePrecision) }%
                      </Typography>
                    </Paper>
                  </Popper>
                </Box>
              )) }
            </Box>)
          ) } */ }
        </Box>

        { /* Render and show the current status of the monitor */ }
        <Typography variant="body2" sx={ { flexShrink: 0,  textAlign: "right", width: "5%" } }>
          <StatusBadge status={ status } />
        </Typography>
      </AccordionSummary>
      { /* Display outage history */ }
      <AccordionDetails style={ { paddingBottom: 0, paddingTop: 0 } }>
        { (features.availabilityHistory && availabilityHistory.length > 0) && (
          (<Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
            { availabilityHistory.map((entry, index) => (
              <Box
                key={ index }
                overflow={ "hidden" }
                bgcolor={ parseFloat(entry.availability) === 100 ? "#28a745" : "#dc3545" }
                borderRadius="5px"
                justifyContent={ "flex-end" }
                width={ "3px" }
                height={ parseFloat(entry.availability) === 100 ? "25px" : "25px" }
                mr={ maxWidth768 ? 0.25 : 0.5 }
                aria-label={ `Availability on ${entry.date} is ${entry.availability}%` }
                onMouseEnter={ event => {
                  if (parseFloat(entry.availability) !== 100) {
                    setPopperDate(entry.date);
                    setPopperAvailability(entry.availability);
                    setAnchorEl(event.currentTarget);
                    setPopperOpen(true);
                  }
                } }
                onMouseLeave={ () => {
                  setPopperOpen(false);
                } }
              >
                <Popper
                  open={ popperOpen }
                  anchorEl={ anchorEl }
                  sx={ { backgroundColor: "rgb(18 18 18)", borderRadius: 8, maxWidth: "200px", minWidth: "200px" } }
                >
                  <Paper sx={ { backgroundColor: "rgb(18 18 18)", p: 2 } } variant="outlined">

                    { /* Outage title component */ }
                    { /* <Typography variant="body2" sx={ { borderBottom: "1px solid", color: "white", pb: 0.5 } }>
                      Service Outage
                    </Typography> */ }

                    <Typography variant="caption" color={ "whitesmoke" }>
                      {
                        new Date(popperDate)
                          .toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })
                      }
                    </Typography>
                    <Typography variant="body2" sx={ { fontWeight: "bold" } } color={ "white" }>
                      Uptime: { parseFloat( Math.floor(popperAvailability * 10 ** window.ENV.uptimePrecision) /
                          10 ** window.ENV.uptimePrecision
                      ).toFixed(window.ENV.uptimePrecision) }%
                    </Typography>
                  </Paper>
                </Popper>
              </Box>
            )) }
          </Box>)
        ) }
      </AccordionDetails>
    </Accordion>
  );
}

SubGroupAccordion.propTypes = {
  availability: PropTypes.array,
  availabilityHistory: PropTypes.array,
  description: PropTypes.string,
  name: PropTypes.string,
  status: PropTypes.number

};

export default SubGroupAccordion;
